// src/encounter/services/use-lab-vendors.ts
// The clinic's lab vendors for the A/P "Sent to" picker (PROD-2653).
//
//   GET /api/Master/LabVendors?search=&includeInactive=false&includeGlobal=false&skip=0&take=200
//   → { items: LabVendorDto[], total }   (Scrips.Master, PROD-2651)
//
// The organization comes from the token's orgId claim, so there is no org parameter. Only ACTIVE,
// org-owned vendors are asked for: global templates are not something a clinician sends a sample to.
// 200 is the endpoint's max page; a clinic has a handful of vendors, so one page is the whole list.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/** One lab vendor, camelCase as Scrips.Master serialises LabVendorDto. */
export interface LabVendor {
  id: string;
  organizationId?: string | null;
  isGlobalTemplate?: boolean;
  name: string;
  shortCode?: string | null;
  type?: 'InHouse' | 'External' | string | null;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  website?: string | null;
  accountNumber?: string | null;
  billingResponsibility?: string | null;
  integration?: string | null;
  codeSystemUri?: string | null;
  defaultTurnaroundDays?: number | null;
  isActive: boolean;
  createdOn?: string;
  updatedOn?: string | null;
}

export interface LabVendorListResult {
  items: LabVendor[];
  total: number;
}

export const LAB_VENDORS_URL =
  '/api/Master/LabVendors?search=&includeInactive=false&includeGlobal=false&skip=0&take=200';

export function useLabVendors() {
  return useQuery<LabVendor[]>({
    queryKey: ['master', 'lab-vendors', 'active'],
    queryFn: async ({ signal }) => {
      const res = await get<LabVendorListResult>(LAB_VENDORS_URL, { signal });
      // Belt and braces: the query already excludes inactive vendors.
      return (res?.items ?? []).filter((v) => v && v.id && v.isActive !== false);
    },
    // Vendors change rarely (master data); one fetch per session is plenty.
    staleTime: 5 * 60 * 1000,
  });
}
